import V from "@/admin/vm/VmDetailView.module.css";
import FF from "@/components/forms/FormField.module.css";
import TF from "@/components/forms/TextField.module.css";

import { Missing, NA, UuidCopyable } from "@/admin/AdminTable.tsx";
import { BillingAccountBlock } from "@/admin/accounts/BillingAccountBlock.tsx";
import { CurrentPool } from "@/admin/host_pools/CurrentPool.tsx";
import { PoolSelect } from "@/admin/host_pools/PoolSelect.tsx";
import { CurrentHost } from "@/admin/hosts/CurrentHost.tsx";
import { HostLink } from "@/admin/hosts/HostLink.tsx";
import { adminHostQueryAtom } from "@/admin/hosts/hostQuery.ts";
import { UserLink } from "@/admin/users/UserLink.tsx";
import { AdminDiskResizeModal } from "@/admin/vm/AdminDiskResizeModal.tsx";
import { DesignatedVmBlock } from "@/admin/vm/DesignatedBlock.tsx";
import { NicIftuneButton } from "@/admin/vm/NicIftuneBlock.tsx";
import type { GQVmItem } from "@/admin/vm/VmsTable.tsx";
import { useGetVmItemForAdmin } from "@/admin/vm/useGetVmItemForAdmin.tsx";
import { vmActionLogQueryAtom } from "@/admin/vm/vmQuery.ts";
import { ClipBoardTooltip } from "@/components/ClipBoardTooltip.tsx";
import { ContentPane } from "@/components/ContentPane.tsx";
import { ResourceWithIcon } from "@/components/ResourceWithIcon.tsx";
import { SectionHeader } from "@/components/SectionHeader.tsx";
import { Separator } from "@/components/Separator.tsx";
import { Spacer } from "@/components/Spacer.tsx";
import { ViewTitle } from "@/components/Title.tsx";
import { WButton } from "@/components/button/WButton.tsx";
import { WModalButton } from "@/components/button/WToolButton.tsx";
import { AdminDate, LongDate } from "@/components/l10n/DateFormat.tsx";
import { ContentLoadingSuspense } from "@/components/loading/Loading.tsx";
import { WModal } from "@/components/modal/WModal.tsx";
import { DetailsHolder, DetailsTable } from "@/components/table/DetailsTable.tsx";
import { DetailsTableRow } from "@/components/table/DetailsTableRow.tsx";
import { WSmartTable, WTable, WTableBody } from "@/components/table/WTable.tsx";
import { useApiClient } from "@/modules/api/apiClient.store.ts";
import { BootMode } from "@/modules/compute/BootMode.tsx";
import { MachineSizeInfo } from "@/modules/compute/MachineSizeInfo.tsx";
import { VmStatusBadge } from "@/modules/compute/VmStatusBadge.tsx";
import { OsInfo } from "@/modules/compute/os/OsInfo.tsx";
import { extractOsFields, type SelectedOs } from "@/modules/compute/os/os.ts";
import { WErrorBoundary } from "@/modules/error/ErrorFallback.tsx";
import { LocationDetail } from "@/modules/location/LocationDetail.tsx";
import type { MachineCommonFields, VmStoragePriceFields } from "@/modules/pricing/resourcePricing.ts";
import { cn } from "@/utils/classNames.ts";
import { useQueryResultAtom, useSuspenseQueryAtom } from "@/utils/query/useSuspenseQueryAtom.ts";
import { apiUnixToDate } from "@warrenio/api-spec/conversion";
import type { VirtualMachineStorage, VmStatusEnum } from "@warrenio/api-spec/spec.oats.gen";
import { getOwn } from "@warrenio/utils/collections/getOwnProperty";
import { assertNotNull, notNull } from "@warrenio/utils/notNull";
import { useState } from "react";
import { TextArea } from "react-aria-components";

export function VmDetailView({ vmUuid, location }: { vmUuid: string; location: string }) {
    const item = notNull(useGetVmItemForAdmin(vmUuid, location), "item");

    return (
        <>
            <VmDetails item={item} />
            <Separator />
            <WErrorBoundary tags="VmDetailView">
                <ContentLoadingSuspense>
                    <VmPoolsHosts item={item} />
                    <Separator />
                    <DisksContent vm={item} />
                    <Separator />
                    <ActionLog item={item} />
                    <Separator />
                    <DomainXml item={item} />
                </ContentLoadingSuspense>
            </WErrorBoundary>
        </>
    );
}

function VmDetails({ item }: { item: GQVmItem }) {
    const {
        name,
        status,
        public_ipv6,
        billing_account_object,
        ip_address_object,
        private_ipv4,
        uuid,
        username,
        mac,
        _location,
        vcpu,
        memory,
        storage,
        os_name,
        os_version,
        boot_firmware,
    } = item;

    assertNotNull(_location);

    return (
        <ContentPane className="VStack gap-4">
            <ViewTitle title={name ?? uuid} />

            <DetailsHolder>
                <DetailsTable>
                    <DetailsTableRow title="Name">{name}</DetailsTableRow>

                    <DetailsTableRow title="Type">
                        <ResourceWithIcon type="virtual_machine" />
                    </DetailsTableRow>

                    <DetailsTableRow title="OS">
                        <OsInfo os={extractOsFields({ os_name, os_version } as SelectedOs)} />
                    </DetailsTableRow>

                    <DetailsTableRow title="Status">
                        <VmStatusBadge value={status as VmStatusEnum} />
                    </DetailsTableRow>

                    {public_ipv6 != null && (
                        <DetailsTableRow title="Public IPv6">
                            <ClipBoardTooltip isHtml>{public_ipv6}</ClipBoardTooltip>
                        </DetailsTableRow>
                    )}

                    {!!ip_address_object?.address && (
                        <DetailsTableRow
                            title="Public IPv4"
                            tooltip="Public IP addresses are managed in the Network view."
                        >
                            <ClipBoardTooltip isHtml>{ip_address_object?.address}</ClipBoardTooltip>
                        </DetailsTableRow>
                    )}

                    <DetailsTableRow title="Private IPv4">
                        <ClipBoardTooltip isHtml>{private_ipv4}</ClipBoardTooltip>
                    </DetailsTableRow>

                    <DetailsTableRow title="Size">
                        <MachineSizeInfo
                            vm={{
                                vcpu,
                                memory,
                                storage: (storage ?? []) as VmStoragePriceFields[],
                                location: _location,
                                status: notNull(status) as MachineCommonFields["status"],
                            }}
                        />
                    </DetailsTableRow>

                    <DetailsTableRow title="Bandwidth limits">
                        <NicIftuneButton item={item} />
                    </DetailsTableRow>
                </DetailsTable>

                <Spacer />

                <DetailsTable>
                    <DetailsTableRow title="UUID">
                        <ClipBoardTooltip>{uuid}</ClipBoardTooltip>
                    </DetailsTableRow>

                    <DetailsTableRow title="Username">{username}</DetailsTableRow>

                    <DetailsTableRow title="MAC">{mac}</DetailsTableRow>

                    <LocationDetail slug={_location} />

                    <DetailsTableRow title="Boot firmware">
                        <BootMode value={boot_firmware} />
                    </DetailsTableRow>

                    <DetailsTableRow title="Created">
                        <LongDate date={apiUnixToDate(item.created_at as number)} />
                    </DetailsTableRow>

                    {typeof item.deleted_at === "number" ? (
                        <DetailsTableRow title="Deleted">
                            <LongDate date={apiUnixToDate(item.deleted_at)} />
                        </DetailsTableRow>
                    ) : (
                        <DetailsTableRow />
                    )}

                    <DetailsTableRow title="Billing account">
                        <BillingAccountBlock showId value={billing_account_object} />
                    </DetailsTableRow>
                </DetailsTable>
            </DetailsHolder>
        </ContentPane>
    );
}

function VmPoolsHosts({ item }: { item: GQVmItem }) {
    const { hypervisor_uuid, designated_pool_uuid, _location } = item;
    assertNotNull(_location);
    const hosts = useSuspenseQueryAtom(adminHostQueryAtom(_location));

    // XXX: Should be asserting that `designated_pool_uuid` is not null
    const initialPoolId = designated_pool_uuid ?? null;
    const [designated, setDesignated] = useState<string | null>(initialPoolId);

    const defaultHost = hypervisor_uuid ? hosts.get(hypervisor_uuid) : null;
    const defaultPoolUuid = defaultHost?.pool_uuid ?? designated_pool_uuid;

    const resetDesignated = () => {
        setDesignated(initialPoolId);
    };

    // XXX: `designated_pool_uuid` should always be set, but due to a backend bug it can be null.
    // As a workaround we just hide the section.
    const designatedSection = designated_pool_uuid && (
        <div className="pt-3">
            <div className="font-size-subtitle pb-2">Designated Pool</div>

            <div className="position-relative">
                <PoolSelect
                    basePoolUuid={designated_pool_uuid}
                    location={_location}
                    valueKey={designated}
                    onChange={(item) => setDesignated(item.uuid)}
                />

                {designated && designated !== designated_pool_uuid && (
                    <div className="position-absolute right-0">
                        <DesignatedVmBlock
                            designated_pool_uuid={designated}
                            item={item}
                            button={<WButton variant="edit" icon="jp-icon-checkmark" action={undefined} />}
                        />
                        <WButton variant="edit" icon="jp-icon-close" action={resetDesignated} />
                    </div>
                )}
            </div>
        </div>
    );

    return (
        <ContentPane>
            <div className={V.FlexBoxes}>
                <div className="flex-1">
                    <div className="font-size-subtitle pb-2">Current Pool</div>

                    <div className="lightBlock p-3">
                        {defaultPoolUuid ? <CurrentPool location={_location} value={defaultPoolUuid} /> : <Missing />}
                    </div>
                    {designatedSection}
                </div>
                <div className="flex-1">
                    <div className="font-size-subtitle pb-2">Current Host</div>

                    <div className="lightBlock p-3">
                        {defaultHost?.uuid ? (
                            <CurrentHost location={_location} value={defaultHost?.uuid} />
                        ) : (
                            <Missing />
                        )}
                    </div>
                </div>
            </div>
        </ContentPane>
    );
}

function DisksContent({ vm }: { vm: GQVmItem }) {
    const { storage } = vm;
    if (!storage) {
        return null;
    }

    const isDeleted = vm.status === "deleted";

    return (
        <ContentPane className="VStack gap-4">
            <SectionHeader title="Disks" />

            <WTable>
                <thead>
                    <tr>
                        <th>UUID</th>
                        <th>Size (GB)</th>
                        <th>Created</th>
                        <th>Mode</th>
                        <th />
                    </tr>
                </thead>
                <WTableBody>
                    {storage.map((item) => (
                        <tr key={item.uuid}>
                            <td>
                                <UuidCopyable value={item.uuid} />
                            </td>
                            <td>{item.size}</td>
                            <td>
                                <AdminDate date={apiUnixToDate(item.created_at as number)} />
                            </td>
                            <td>{item.primary ? "Boot" : "-"}</td>
                            <td className="text-right">
                                <WModal
                                    button={
                                        <WModalButton
                                            label="Resize"
                                            isDisabled={isDeleted}
                                            inTable
                                            icon="jp-expand-2-icon"
                                        />
                                    }
                                >
                                    <AdminDiskResizeModal vm={vm} disk={item as VirtualMachineStorage} />
                                </WModal>
                            </td>
                        </tr>
                    ))}
                </WTableBody>
            </WTable>
        </ContentPane>
    );
}

const actionColors: Record<string, string> = {
    stop: "color-error",
    stop_forceful: "color-error",
    start: "color-success",
};

function ActionLog({ item }: { item: GQVmItem }) {
    const location = notNull(item._location);

    const { data: logs, refetch } = useQueryResultAtom(vmActionLogQueryAtom(location, item.uuid));

    // DEBT: Have to query all hosts to get the name since the action log only contains host UUIDs
    const { data: hosts } = useQueryResultAtom(adminHostQueryAtom(location));

    async function refreshAction() {
        await refetch();
    }

    return (
        <ContentPane className="VStack gap-3">
            <div className="flex justify-between items-center">
                <div className="font-size-subtitle">Action log</div>

                <WButton action={refreshAction} color="primary" size="md" variant="ghost" icon="i-lucide:refresh-cw">
                    Reload Logs
                </WButton>
            </div>

            <WSmartTable
                items={logs}
                hasViewAllButton
                head={
                    <thead>
                        <tr>
                            <th>Action</th>
                            <th>Created At</th>
                            <th>Hypervisor</th>
                            <th>Hypervisor UUID</th>
                            <th>Acting User ID</th>
                        </tr>
                    </thead>
                }
            >
                {(entry, index) => {
                    const { action, created_at, hypervisor_uuid, acting_user_id } = entry;

                    const host = hypervisor_uuid ? hosts?.get(hypervisor_uuid) : undefined;

                    const isOwner = acting_user_id != null && item.user_id === acting_user_id;
                    const userLabel = isOwner ? <span className="text-muted">Owner</span> : undefined;

                    const color = getOwn(actionColors, action);

                    return (
                        <tr key={index}>
                            <td>
                                <span className={color}>{action}</span>
                            </td>
                            <td>
                                <AdminDate date={created_at} />
                            </td>
                            <td>{host ? <HostLink item={host} /> : <NA />}</td>
                            <td>{hypervisor_uuid ? <UuidCopyable value={hypervisor_uuid} /> : <NA />}</td>
                            <td>
                                <UserLink value={acting_user_id} label={userLabel} />
                            </td>
                        </tr>
                    );
                }}
            </WSmartTable>
        </ContentPane>
    );
}

function DomainXml({ item }: { item: GQVmItem }) {
    const { uuid, _location } = item;
    assertNotNull(_location);

    const api = useApiClient();
    const [xml, setXml] = useState<string>();

    const getXml = async () => {
        const response = await api.GET("/{location}/base-operator/admin/vm/virsh_dumpxml", {
            params: { query: { uuid }, path: { location: _location } },
            parseAs: "text",
        });
        setXml(response.data);
    };

    const isDeleted = item.status === "deleted";

    return (
        <ContentPane className="VStack gap-3">
            <div className="flex justify-between items-center">
                <div className="font-size-subtitle">Domain XML</div>

                <WButton
                    isDisabled={item.status === "stopped" || isDeleted}
                    action={() => getXml()}
                    color="primary"
                    size="md"
                    variant="ghost"
                    icon={xml ? "i-lucide:refresh-cw" : "i-lucide:arrow-down-to-line"}
                >
                    {xml ? "Reload XML" : "Load XML"}
                </WButton>
            </div>

            <p className="color-muted">
                Currently effective domain XML that describes this Virtual Machine in libvirt, useful for debugging.
                Available only if VM is defined on some hypervisor, usually not available for stopped VMs.
            </p>

            {!!xml && (
                <div>
                    <TextArea rows={12} value={xml} readOnly className={cn(FF.FormFieldTextArea, TF.TextArea)} />
                </div>
            )}
        </ContentPane>
    );
}
